// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Field-name alignment for the ADR-004 D3 unconditional AU0001 read (amended
 * by #35), mirroring how ../contracts/mock-transport and
 * updater-au-response-mapper.ts already reconcile a raw foreign-AU
 * response_payload onto a widget contract shape.
 *
 * The evidenced au0001_register_offline_update_log.AttributionAPIv1
 * .GetLatestAttributionByDeviceAnchor capability itself returns
 * `{ success, data: { device_anchor, latest: {...}, source_intake: {...},
 * attribution_list: [...] } }`. But by the time this mapper sees it, the
 * `{success, data}` envelope has already been stripped: rendezvous.ts's
 * readFileObject unwraps a non-null `data` object one level before returning
 * it (the same rule updater-au-response-mapper.ts's `rec["firmware_package_ref"]`
 * already relies on for AU0008/AU0004), so `raw` here IS that inner object
 * directly:
 *
 *   { device_anchor, latest: { entry_id, device_anchor, execution_status,
 *     operation_ts, attribution_ts, source_intake, resolved_contract,
 *     provenance_method, supersedes_entry_id }, source_intake: {...},
 *     attribution_list: [...] }
 *
 * `latest.execution_status` (D1/D3 eligibility gate) and `latest.operation_ts`
 * (D3 selection timestamp) are read here for D3's selection. `source_intake
 * .file_name` is ALSO read (decided alongside #25/#27, 2026-07-28): once
 * AU0001 wins D3, the widget shows this device's operation_ts and the
 * uploaded offline-log file name it came from as that winner's own display
 * fields (ADR-004 D4 mapping), so both are carried through here, at the one
 * place this read's raw field spellings are reconciled.
 *
 * `source_intake.created_by`/`added_time` (who/when transferred that file)
 * and `attribution_list` (the device's fuller offline attribution history)
 * are read here too, as a provisional LOCAL display addition (agreed
 * 2026-08-10) to give the operator the most consistent picture of offline
 * activity. This reverses the D6 amendment's prior "attribution_list /
 * correction-chain bookkeeping stay out of scope" note below `source_intake`
 * .file_name's own use above — pending a formal ADR-004 amendment to make
 * that scope change official; until then this is a pre-ADR display change,
 * not a new selection input (only `latest` still competes in D3).
 *
 * `operation_ts` arrives as a Zoho-formatted date string (`DD-MMM-YYYY
 * HH:mm:ss`, e.g. "21-May-2026 07:58:29"), NOT ISO 8601 — unlike the ISO
 * strings assumed elsewhere in this widget's mocks. The orchestrator's D3
 * comparison uses `Date.parse`, which does not reliably parse that format
 * across engines, so it is converted to ISO 8601 here, at the one place the
 * real transport meets AU0001's raw field spellings.
 */

import type { OfflineCandidateResponse } from "@/contracts/requests";

function toStringOrEmpty(value: unknown): string {
  return typeof value === "string" ? value : "";
}

const ZOHO_MONTHS: Readonly<Record<string, string>> = {
  Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
  Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
};

const ZOHO_DATE_PATTERN = /^(\d{2})-([A-Za-z]{3})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/;

/**
 * Convert AU0001's `DD-MMM-YYYY HH:mm:ss` timestamp to an ISO 8601 string
 * (`Date.parse`-safe). Returns undefined on anything that does not match the
 * evidenced shape, rather than guessing — an unparseable timestamp simply
 * does not compete in D3 (mirrors D1's "empty is not a halt"), it is not
 * silently coerced to a wrong instant.
 */
function toIsoFromZohoDate(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const match = ZOHO_DATE_PATTERN.exec(raw.trim());
  if (!match) return undefined;
  const [, day, monthName, year, hh, mm, ss] = match;
  const month = ZOHO_MONTHS[monthName as string];
  if (!month) return undefined;
  return `${year}-${month}-${day}T${hh}:${mm}:${ss}Z`;
}

/**
 * Map a raw AU0001 attribution response_payload onto OfflineCandidateResponse.
 * Idempotent: a payload that already carries a `found` field (e.g. the mock
 * transport's contract-shaped fixtures) passes through unchanged.
 *
 * A missing/malformed `latest` entry (AU0001 responded successfully but has
 * no attribution for this device anchor — readFileObject then hands back the
 * surviving `{ success, data: null }` envelope, which has no `latest` key
 * either) or an unparseable `operation_ts` both map to `{ found: false }` — a
 * valid, non-error D3 input (ADR-004 D1's "empty is not a halt", extended to
 * this unconditional read), never a thrown/reported failure. Presence of
 * `latest` is the signal, not a top-level `success` flag: mirrors
 * updater-au-response-mapper.ts, which likewise never gates on `success`
 * being a strict boolean, since the exact JSON-vs-string typing that survives
 * the Deluge → bridge round-trip is not guaranteed (ISO/IEC 5055 — do not
 * assume a wire-format detail that isn't actually fixed by any ADR).
 */
export function mapAu0001AttributionResponse(raw: unknown): OfflineCandidateResponse {
  if (typeof raw !== "object" || raw === null) {
    return { found: false };
  }
  const rec = raw as Record<string, unknown>;

  // Already-mapped pass-through (e.g. mock transport fixtures).
  if ("found" in rec) {
    return rec as unknown as OfflineCandidateResponse;
  }

  // readFileObject (rendezvous.ts) already unwraps the {success, data}
  // envelope down to the inner data object when data is non-null, so `rec`
  // here IS that object directly — read `latest` off it, not `rec.data.latest`.
  const latest = rec["latest"];
  if (typeof latest !== "object" || latest === null) {
    return { found: false };
  }
  const entry = latest as Record<string, unknown>;

  const operationTs = toIsoFromZohoDate(entry["operation_ts"]);
  if (operationTs === undefined) {
    return { found: false };
  }

  // `source_intake` is a nested object with `latest` alongside it on the
  // reconciled payload (see this file's header); a missing/malformed
  // source_intake simply leaves sourceFileName/sourceTransferredBy/
  // sourceTransferTime unset — the display later renders that as "unknown"
  // (ADR-004 D4), never a thrown/reported failure.
  //
  // `created_by` (who transferred/uploaded the file) and `added_time` (when)
  // are read alongside `file_name` here as a provisional local display
  // addition (see OfflineWinnerInfo in ../contracts/display), pending a
  // formal ADR-004 amendment to bring `source_intake` beyond `file_name`
  // into scope.
  const sourceIntake = rec["source_intake"];
  const sourceIntakeRec =
    typeof sourceIntake === "object" && sourceIntake !== null
      ? (sourceIntake as Record<string, unknown>)
      : undefined;
  const fileName = sourceIntakeRec?.["file_name"];
  const transferredBy = sourceIntakeRec?.["created_by"];
  // `added_time` is assumed to arrive in the same Zoho date format as
  // `latest.operation_ts` (no evidenced counter-example); an unparseable
  // value simply leaves sourceTransferTime unset rather than failing the
  // whole read, mirroring how a missing source_intake is handled above.
  const transferTimeIso = toIsoFromZohoDate(sourceIntakeRec?.["added_time"]);

  // Verbatim raw Zoho date string (NOT the ISO `operationTs` above) — carried
  // through only to anchor the D6 ListFirmwarePackageMeta tsBoundary (#27) in
  // whatever raw format that capability expects, since the resolver relays
  // request_payload fields unparsed (ADR-002 P6).
  const operationTsRaw = entry["operation_ts"];

  // `attribution_list` — the device's fuller offline attribution history.
  // Provisional local display addition (see OfflineAttributionEntry in
  // ../contracts/display): previously out of scope per this file's D6
  // amendment note above; surfaced here pending a formal ADR-004 amendment.
  // A missing/malformed list, or an individual entry missing either field,
  // is simply dropped/left partial rather than failing the whole read —
  // same "empty is not a halt" posture as the rest of this mapper.
  const attributionListRaw = rec["attribution_list"];
  const attributionList = Array.isArray(attributionListRaw)
    ? attributionListRaw
        .filter(
          (item): item is Record<string, unknown> =>
            typeof item === "object" && item !== null,
        )
        .map((item) => ({
          executionStatus: toStringOrEmpty(item["execution_status"]),
          operationTs: toIsoFromZohoDate(item["operation_ts"]) ?? "",
        }))
    : undefined;

  return {
    found: true,
    operationTs,
    executionStatus: toStringOrEmpty(entry["execution_status"]),
    ...(typeof fileName === "string" && fileName.trim().length > 0
      ? { sourceFileName: fileName }
      : {}),
    ...(typeof transferredBy === "string" && transferredBy.trim().length > 0
      ? { sourceTransferredBy: transferredBy }
      : {}),
    ...(transferTimeIso !== undefined
      ? { sourceTransferTime: transferTimeIso }
      : {}),
    ...(typeof operationTsRaw === "string" && operationTsRaw.trim().length > 0
      ? { operationTsRaw }
      : {}),
    ...(attributionList !== undefined && attributionList.length > 0
      ? { attributionList }
      : {}),
  };
}
